import { onlineManager, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getCachedSelfAppDataOptions } from './user-query-options';

const SETTLE_TIMEOUT_MS = 10_000;

/**
 * Whether the self app data has been re-fetched since this component mounted (FR-013).
 *
 * The persisted cache is up to 24h old at boot and the shared options disable
 * refetch-on-mount, so a venue granted since the last session looks revoked until this
 * settles. Access verdicts — refusals, redirects, forced logout — must wait for it.
 *
 * Latches on a completed fetch whether it succeeded or failed, immediately while offline,
 * and on a `SETTLE_TIMEOUT_MS` ceiling: without data there is nothing better than the
 * cached list, and blocking navigation forever is worse than deciding on it. The ceiling is
 * what covers a request that never answers — retries are capped, but a cold API behind a
 * captive portal neither resolves nor takes `onlineManager` offline.
 *
 * `enabled` false skips the boot fetch entirely and reports settled — for a caller whose
 * verdicts do not depend on the answer, so an offline-capable app is not made to reach the
 * network at boot for a value it never reads.
 */
export const useSelfAppDataSettled = (enabled: boolean = true): boolean => {
  const { isFetchedAfterMount } = useQuery({
    ...getCachedSelfAppDataOptions(),
    refetchOnMount: 'always',
    enabled,
  });

  const [isSettled, setIsSettled] = useState(() => !onlineManager.isOnline());

  useEffect(() => {
    if (!enabled || isFetchedAfterMount) {
      setIsSettled(true);
      return;
    }

    const unsubscribe = onlineManager.subscribe((isOnline) => {
      if (!isOnline) setIsSettled(true);
    });
    const ceiling = setTimeout(() => setIsSettled(true), SETTLE_TIMEOUT_MS);

    return () => {
      unsubscribe();
      clearTimeout(ceiling);
    };
  }, [enabled, isFetchedAfterMount]);

  return !enabled || isSettled;
};
